import { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CountryMultiSelect } from "../components/CountryMultiSelect";
import {
  createRule,
  deleteRule,
  getRule,
  getStatus,
  listRules,
  listStores,
  publishConfig,
  updateRule,
  updateSettings,
} from "../lib/safegeo-api";
import { countryName, isEeaCountry } from "../lib/countries";
import { hasFeature, isFreePlan } from "../lib/format";
import { fieldChecked, fieldValue } from "../lib/polaris";
import { queryKeys } from "../lib/query-keys";
import { SafePage } from "../components/SafePage";
import { AppButton } from "../components/AppButton";
import { AppLink } from "../components/AppLink";
import { ErrorBanner, InfoBanner } from "../components/PageBanner";
import { SaveBarHost } from "../components/SaveBarHost";
import { useSaveBar } from "../lib/save-bar";
import { showToast } from "../lib/feedback";

const SAVE_BAR_ID = "safegeo-rule-form-save";

function isValidHttpsUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "https:";
  } catch {
    return false;
  }
}

/** Final P0 #1 - rule form is website vs block only; Ask/Auto lives on Market/Store pages. */
type FormKind = "website" | "block";

export function RuleFormPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const onBlockRoute = location.pathname.includes("/app/block");
  const defaultKind: FormKind =
    onBlockRoute || params.get("action") === "block" ? "block" : "website";

  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const rulesQ = useQuery({ queryKey: queryKeys.rules, queryFn: listRules });
  const ruleQ = useQuery({
    queryKey: queryKeys.rule(id || ""),
    queryFn: () => getRule(id!),
    enabled: !isNew && !!id,
  });
  const storesQ = useQuery({
    queryKey: queryKeys.stores,
    queryFn: listStores,
    enabled: !!statusQ.data?.multi_store_unlocked,
  });

  const [selected, setSelected] = useState<string[]>([]);
  const [destination, setDestination] = useState("https://");
  const [kind, setKind] = useState<FormKind>(defaultKind);
  /** Final P0 #3 - default off for another website. */
  const [keepPath, setKeepPath] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    countries?: string;
    destination?: string;
  }>({});
  const [busy, setBusy] = useState(false);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [legal, setLegal] = useState(false);
  const [euPending, setEuPending] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  /** On-form Ask / Send (mirrors Store redirect markets_auto when saving website). */
  const [sendAuto, setSendAuto] = useState(false);

  useEffect(() => {
    if (ruleQ.data) {
      setSelected(ruleQ.data.countries || []);
      setDestination(ruleQ.data.destination_url || "https://");
      setKind(ruleQ.data.action === "block" ? "block" : "website");
      setKeepPath(ruleQ.data.keep_path === true);
      setLegal(!!ruleQ.data.legal);
    }
  }, [ruleQ.data]);

  useEffect(() => {
    if (statusQ.data) {
      setSendAuto(!!statusQ.data.settings?.markets_auto);
    }
  }, [statusQ.data]);

  const draftSnap = useMemo(
    () =>
      JSON.stringify({
        selected: [...selected].map((c) => c.toUpperCase()).sort(),
        destination,
        kind,
        keepPath,
        legal,
      }),
    [selected, destination, kind, keepPath, legal],
  );

  useEffect(() => {
    if (isNew && baseline == null) {
      setBaseline(draftSnap);
      return;
    }
    if (!isNew && ruleQ.data && baseline == null) setBaseline(draftSnap);
  }, [isNew, ruleQ.data, draftSnap, baseline]);

  const dirty = baseline != null && draftSnap !== baseline;
  useSaveBar(SAVE_BAR_ID, dirty);

  const features = statusQ.data?.entitlements?.features;
  const free = isFreePlan(statusQ.data?.plan, statusQ.data?.plan_key);
  const featOpts = {
    plan: statusQ.data?.plan,
    planKey: statusQ.data?.plan_key,
  };
  const canOther = hasFeature(features, "otherWebsite", featOpts);
  const canBlock = hasFeature(features, "blockCountries", featOpts);
  const marketsAuto = !!statusQ.data?.settings?.markets_auto;

  const duplicateCountries = useMemo(() => {
    const others = (rulesQ.data?.items || []).filter(
      (r) =>
        !r.is_fallback &&
        r.is_enabled &&
        r.id !== id &&
        (r.countries || []).some((c) =>
          selected.map((x) => x.toUpperCase()).includes(c.toUpperCase()),
        ),
    );
    if (!others.length) return [] as string[];
    return selected
      .map((c) => c.toUpperCase())
      .filter((c) =>
        others.some((r) =>
          (r.countries || []).map((x) => x.toUpperCase()).includes(c),
        ),
      );
  }, [rulesQ.data?.items, selected, id]);

  const usedElsewhere = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of rulesQ.data?.items || []) {
      if (r.is_fallback || !r.is_enabled || r.id === id) continue;
      const label =
        r.action === "block" ? "Used in Block" : "Used in Store redirect";
      for (const c of r.countries || []) {
        map[c.toUpperCase()] = label;
      }
    }
    return map;
  }, [rulesQ.data?.items, id]);

  const linkedStores = useMemo(() => {
    return (storesQ.data?.items || [])
      .filter(
        (s) =>
          s.role !== "primary" && (s.storefront_url || s.shop_domain),
      )
      .map((s) => {
        const url =
          s.storefront_url ||
          `https://${s.shop_domain.replace(/^https?:\/\//i, "")}`;
        return {
          id: s.shop_id,
          label: s.shop_domain.replace(/\.myshopify\.com$/i, ""),
          url,
        };
      });
  }, [storesQ.data?.items]);

  function onCountriesChange(next: string[]) {
    const normalized = next.map((c) => c.toUpperCase());
    if (kind !== "block" && !onBlockRoute) {
      setSelected(normalized);
      return;
    }
    const added = normalized.filter((c) => !selected.includes(c));
    const eeaNew = added.filter(isEeaCountry);
    if (eeaNew.length && !legal) {
      setLegal(false);
      setEuPending(eeaNew);
      return;
    }
    setSelected(normalized);
  }

  function confirmEuBlock() {
    setLegal(true);
    setSelected((prev) => {
      const next = [...prev];
      for (const c of euPending) {
        if (!next.includes(c)) next.push(c);
      }
      return next;
    });
    setEuPending([]);
  }

  if (
    statusQ.isSuccess &&
    isNew &&
    ((!canOther && kind !== "block") || (kind === "block" && !canBlock))
  ) {
    return (
      <SafePage screen="rule-form" heading="Country rule">

        <InfoBanner heading="Available on Starter and above">
          {kind === "block"
            ? "Blocking countries starts on Starter. Free uses Shopify Markets ask-first - configure that under Market redirect and Popup."
            : "Redirecting to another website you own starts on Starter. Free uses Shopify Markets ask-first - no external rule required."}
        </InfoBanner>
        <s-box paddingBlockStart="base">
          <s-stack direction="inline" gap="base">
            <AppButton variant="primary" to="/app/plans">
              See plans
            </AppButton>
            <AppButton
              variant="tertiary"
              to={kind === "block" || onBlockRoute ? "/app/block" : "/app/stores"}
            >
              Back
            </AppButton>
            <AppButton variant="tertiary" to="/app/popup">
              Popup settings
            </AppButton>
          </s-stack>
        </s-box>
      </SafePage>
    );
  }

  async function saveRule() {
    setError(null);
    const nextErrors: { countries?: string; destination?: string } = {};
    const list = selected.map((c) => c.toUpperCase()).filter(Boolean);
    if (list.length === 0) {
      nextErrors.countries = "Select at least one country.";
    }
    if (list.some((c) => !/^[A-Z]{2}$/.test(c))) {
      nextErrors.countries = "Use valid ISO country codes.";
    }
    if (duplicateCountries.length) {
      const names = [...new Set(duplicateCountries)]
        .map((c) => countryName(c) || c)
        .join(", ");
      nextErrors.countries = `${names} is already in another row.`;
    }

    if (kind === "block" && !canBlock) {
      setError("Blocking countries needs Starter.");
      return;
    }
    if (kind !== "block" && !canOther) {
      setError("Sending visitors to another website needs Starter.");
      return;
    }
    if (kind !== "block") {
      if (
        !destination ||
        destination === "https://" ||
        destination === "http://"
      ) {
        nextErrors.destination = "Enter a destination URL.";
      } else if (!isValidHttpsUrl(destination.trim())) {
        nextErrors.destination =
          "Enter a full URL starting with https://";
      }
    }

    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        countries: list,
        // Server remaps website → ask/auto from Where they go mode.
        action: kind === "block" ? "block" : "redirect",
        keep_path: kind === "block" ? false : keepPath,
        keep_query: false,
        destination_url: kind === "block" ? null : destination.trim(),
        legal:
          kind === "block" && selected.some(isEeaCountry) ? true : undefined,
      };
      if (isNew) {
        await createRule(body);
      } else {
        await updateRule(id!, body);
      }
      if (kind !== "block" && sendAuto !== marketsAuto) {
        try {
          await updateSettings({ markets_auto: sendAuto });
        } catch {
          /* rule saved; mode may lag */
        }
      }
      setBaseline(draftSnap);
      showToast(isNew ? "Country rule saved" : "Country rule updated");
      try {
        await publishConfig();
        navigate(`${listPath}?saved=1`);
      } catch {
        navigate(`${listPath}?saved=1&publish_warning=1`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function onDiscard() {
    if (isNew) {
      setSelected([]);
      setDestination("https://");
      setKind(defaultKind);
      setKeepPath(false);
      setBaseline(
        JSON.stringify({
          selected: [],
          destination: "https://",
          kind: defaultKind,
          keepPath: false,
        }),
      );
    } else if (ruleQ.data) {
      setSelected(ruleQ.data.countries || []);
      setDestination(ruleQ.data.destination_url || "https://");
      setKind(ruleQ.data.action === "block" ? "block" : "website");
      setKeepPath(ruleQ.data.keep_path === true);
      setBaseline(null);
    }
    setError(null);
    setFieldErrors({});
  }

  async function onDelete() {
    if (!id || isNew) return;
    if (!confirm("Delete this country rule?")) return;
    setBusy(true);
    try {
      await deleteRule(id);
      showToast("Country rule deleted");
      try {
        await publishConfig();
      } catch {
        /* ok */
      }
      navigate(`${listPath}?saved=1`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const isBlock = kind === "block" || onBlockRoute;
  const listPath = isBlock ? "/app/block" : "/app/stores";

  const linkedStoreSelected = linkedStores.some((s) => s.url === destination);

  const countryNames =
    selected.length === 0
      ? "selected countries"
      : selected
          .slice(0, 3)
          .map((c) => countryName(c) || c)
          .join(", ") +
        (selected.length > 3 ? ` and ${selected.length - 3} more` : "");

  const whatWillHappen = isBlock
    ? `Visitors from ${countryNames} see your block page and can’t browse this store.`
    : sendAuto
      ? `Visitors from ${countryNames} are sent automatically to ${destination && destination !== "https://" ? destination : "the destination URL"} (counts toward quota).`
      : `Visitors from ${countryNames} are asked first, then sent to ${destination && destination !== "https://" ? destination : "the destination URL"} if they switch.`;

  const saveBlockedReason = (() => {
    if (free) return "Upgrade to Starter to save this rule.";
    if (selected.length === 0) return "To save: pick at least one country.";
    if (duplicateCountries.length)
      return "To save: remove countries already used in another rule.";
    if (isBlock && !canBlock) return "To save: Blocking needs Starter.";
    if (!isBlock && !canOther)
      return "To save: Other websites need Starter.";
    if (
      !isBlock &&
      (!destination ||
        destination === "https://" ||
        destination === "http://" ||
        !isValidHttpsUrl(destination.trim()))
    ) {
      return "To save: enter a full https:// destination URL.";
    }
    return null;
  })();
  const canSave = !busy && !saveBlockedReason;

  return (
    <SafePage
      screen="rule-form"
      heading={
        isNew
          ? "Add country rule"
          : isBlock
            ? "Edit country block"
            : "Edit country rule"
      }
      breadcrumb={
        <s-link slot="breadcrumb-actions" href={listPath}>
          {isBlock ? "Block country" : "Store redirect"}
        </s-link>
      }
      primaryAction={
        <AppButton
          slot="primary-action"
          disabled={!canSave}
          loading={busy}
          onClick={() => void saveRule()}
        >
          Save country rule
        </AppButton>
      }
      secondaryActions={
        <AppButton slot="secondary-actions" to={listPath}>
          Cancel
        </AppButton>
      }
    >
      <SaveBarHost
        id={SAVE_BAR_ID}
        saving={busy}
        onSave={() => void saveRule()}
        onDiscard={onDiscard}
      />
{free ? (
        <InfoBanner heading="Needs Starter or higher">
          Other-website and block rules need a paid plan.{" "}
          <AppButton variant="tertiary" to="/app/plans">
            See plans
          </AppButton>
        </InfoBanner>
      ) : null}
      {error ? (
        <ErrorBanner heading="Couldn’t save">{error}</ErrorBanner>
      ) : null}
      {duplicateCountries.length ? (
        <ErrorBanner heading="Country already used">
          {[...new Set(duplicateCountries)]
            .map((c) => countryName(c) || c)
            .join(", ")}{" "}
          is already in another row. Remove it there first.
        </ErrorBanner>
      ) : null}

      <s-section heading="Rule type">
        <s-stack direction="block" gap="small">
          <s-button
            type="button"
            variant={kind === "website" ? "primary" : "secondary"}
            onClick={() => setKind("website")}
          >
            Another website
          </s-button>
          {linkedStores.length > 0 ? (
            <s-button
              type="button"
              variant={
                kind === "website" && linkedStoreSelected
                  ? "primary"
                  : "secondary"
              }
              onClick={() => {
                setKind("website");
                const first = linkedStores[0];
                if (first) setDestination(first.url);
              }}
            >
              A connected store
            </s-button>
          ) : null}
          <s-button
            type="button"
            variant={kind === "block" ? "primary" : "secondary"}
            disabled={!canBlock}
            onClick={() => setKind("block")}
          >
            {canBlock ? "Block" : "Block · Starter"}
          </s-button>
        </s-stack>
      </s-section>

      <s-section heading="Countries">
        <CountryMultiSelect
          value={selected}
          onChange={onCountriesChange}
          disabled={busy}
          error={fieldErrors.countries}
          usedElsewhere={usedElsewhere}
        />
        {isBlock ? (
          <s-paragraph color="subdued">
            Search bots aren’t blocked, so Google can still index your store.
          </s-paragraph>
        ) : (
          <s-paragraph color="subdued">
            These visitors are{" "}
            <s-text type="strong">
              {marketsAuto ? "sent automatically" : "asked first"}
            </s-text>{" "}
            · <AppLink to="/app/stores">Change for all</AppLink>
            {marketsAuto ? (
              <>
                {" "}
                EU visitors are always asked first, for EU privacy rules. You
                can change this in Settings.
              </>
            ) : null}
          </s-paragraph>
        )}
      </s-section>

      {euPending.length > 0 ? (
        <s-box padding="base" borderWidth="base" borderRadius="base">
          <s-stack direction="block" gap="base">
            <s-text type="strong">Block EU countries?</s-text>
            <s-paragraph>
              Blocking{" "}
              {euPending.map((c) => countryName(c)).join(", ")} by location
              needs a legal reason, like sanctions or product restrictions.
            </s-paragraph>
            <s-checkbox
              label="I have a legal reason"
              checked={legal}
              onChange={() => setLegal((v) => !v)}
            />
            <s-stack direction="inline" gap="base">
              <s-button
                type="button"
                variant="secondary"
                onClick={() => {
                  setEuPending([]);
                  setLegal(!!ruleQ.data?.legal);
                }}
              >
                Cancel
              </s-button>
              <s-button
                type="button"
                variant="primary"
                tone="critical"
                disabled={!legal}
                onClick={confirmEuBlock}
              >
                Block
              </s-button>
            </s-stack>
          </s-stack>
        </s-box>
      ) : null}

      {!isBlock ? (
        <s-section heading="What happens">
          <s-stack direction="inline" gap="small">
            <s-button
              type="button"
              variant={!sendAuto ? "primary" : "secondary"}
              onClick={() => setSendAuto(false)}
            >
              Ask first (recommended)
            </s-button>
            <s-button
              type="button"
              variant={sendAuto ? "primary" : "secondary"}
              onClick={() => setSendAuto(true)}
            >
              Send automatically
            </s-button>
          </s-stack>
          <s-paragraph color="subdued">
            {sendAuto
              ? "Counts toward your monthly automatic redirect quota. Cart, ad clicks, and EU visitors are still asked first."
              : "Visitor chooses in the popup; their choice never counts toward quota."}
          </s-paragraph>
        </s-section>
      ) : null}

      {isBlock ? (
        <s-section heading="What blocked visitors see">
          <s-paragraph color="subdued">
            They see your Block page. Edit the title and message on Block
            country.
          </s-paragraph>
          <AppButton variant="secondary" to="/app/block">
            Edit the Block page
          </AppButton>
        </s-section>
      ) : (
        <s-section
          heading="Destination URL"
          subheading="Must start with https://"
        >
          {linkedStores.length > 0 ? (
            <s-box paddingBlockEnd="base">
              <s-text color="subdued">Connected stores</s-text>
              <s-stack direction="inline" gap="small">
                {linkedStores.map((s) => (
                  <s-button
                    key={s.id}
                    type="button"
                    variant={
                      destination === s.url ? "primary" : "tertiary"
                    }
                    onClick={() => setDestination(s.url)}
                  >
                    {s.label}
                  </s-button>
                ))}
              </s-stack>
            </s-box>
          ) : (
            <s-paragraph color="subdued">
              No linked store yet. Connect one under{" "}
              <AppLink to="/app/stores">Store redirect</AppLink>, or paste any
              URL you own below.
            </s-paragraph>
          )}
          <s-url-field
            label="Destination URL"
            value={destination}
            onChange={(e: Event) => setDestination(fieldValue(e))}
            details="https://yourbrand.co.uk"
            placeholder="https://yourbrand.co.uk"
            error={fieldErrors.destination}
          />
          <s-box paddingBlockStart="base">
            <s-button
              type="button"
              variant="tertiary"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? "Hide advanced" : "Advanced"}
            </s-button>
            {showAdvanced || keepPath ? (
              <s-box paddingBlockStart="small">
                <s-checkbox
                  label="Keep the same page path"
                  checked={keepPath}
                  onChange={(e: Event) => setKeepPath(fieldChecked(e))}
                  details="Off by default. When off, shoppers land on the destination homepage."
                />
              </s-box>
            ) : null}
          </s-box>
        </s-section>
      )}

      <s-section heading="What will happen">
        <s-paragraph>{whatWillHappen}</s-paragraph>
        {saveBlockedReason ? (
          <s-paragraph color="subdued">{saveBlockedReason}</s-paragraph>
        ) : null}
      </s-section>

      {!isNew ? (
        <s-box paddingBlockStart="base">
          <s-button
            variant="tertiary"
            tone="critical"
            disabled={busy}
            onClick={() => void onDelete()}
          >
            Delete rule
          </s-button>
        </s-box>
      ) : null}
    </SafePage>
  );
}
